import React, { ReactNode, useEffect } from 'react';
import Section from '../../layout/section/section';
import Button from '../../ui/button/button';
import TextField from '../../ui/text-field/text-field';
import Preloader from '../preloader/preloader';
import {
  ArrowRepeat,
  BoxArrowUpRight,
  CheckCircleFill,
  Envelope,
  ExclamationTriangleFill,
  GeoAltFill,
  HouseFill,
  ThermometerHalf,
  ThermometerHigh,
  Twitter,
  XCircleFill,
} from 'react-bootstrap-icons';
import HeatIslandImage from '../heat-island-image/heat-island-image';
import Checkbox from '../../ui/checkbox/checkbox';
import { cropImage, formatToTwoDecimals, validateEmail } from '../../../helpers/utils';
import madriwaService from '../../../services/madriwa-service/madriwa-service';
import { LTSZoneDto } from '../../../services/madriwa-service/LTSZoneDto';

type ResultSectionProps = {
  locationName?: string;
  locationPosition?: { lat: number; lng: number };
  onNewSearch?: () => void;
};

export const ResultSection: React.FC<ResultSectionProps> = ({
  locationName,
  locationPosition,
  onNewSearch,
}) => {
  const [isRecoveringHeatIsland, setIsRecoveringHeatIsland] = React.useState<boolean>(true);
  const [userMail, setUserMail] = React.useState<string>();
  const [isNotARobot, setIsNotARobot] = React.useState<boolean>(false);

  const [heatIslandInfo, setHeatIslandInfo] = React.useState<LTSZoneDto>();
  const [isAHeatIsland, setIsAHeatIsland] = React.useState<boolean>();
  const [croppedImage, setCroppedImage] = React.useState<string>();

  const [isSubscribed, setIsSubscribed] = React.useState<boolean>();
  const [isSubscribing, setIsSubscribing] = React.useState<boolean>();
  const [subscribingRequestAnswer, setSubscribingRequestAnswer] = React.useState<string>();

  useEffect(() => {
    recoverData();
  }, [locationPosition]);

  const recoverData = () => {
    locationPosition &&
      madriwaService
        .getHeatIslandInfo(locationPosition.lng, locationPosition.lat)
        .then((heatIslandInfo: LTSZoneDto) => {
          setHeatIslandInfo(heatIslandInfo);
          setIsAHeatIsland(heatIslandInfo.values.point.lts > heatIslandInfo.values.mean);
          heatIslandInfo.image &&
            cropImage(
              `data:image/jpeg;base64,${heatIslandInfo.image}`,
              144,
              59,
              369,
              368,
              (image) => setCroppedImage(image)
            );
          setIsRecoveringHeatIsland(false);
        })
        .catch((error: any) => {
          setIsRecoveringHeatIsland(false);
        });
  };

  const onNewSearchHandler = () => {
    onNewSearch && onNewSearch();
  };

  const isValidForm = () => {
    return userMail && validateEmail(userMail) && isNotARobot;
  };

  const onTweetMessage = () => {
    const tweetMessage = `¡Hola! He descubierto que mi casa ${
      isAHeatIsland ? 'es' : 'no es'
    } una isla de calor relativa. ¿Quieres saber si la tuya también lo es? ¡Descúbrelo en https://isladecalor.taniwa.es/!`;
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURI(tweetMessage)}`;
    window.open(tweetUrl, '_blank');
  };

  const onChangeEmail = (value: string) => {
    setIsSubscribing(undefined);
    setIsSubscribed(undefined);
    setUserMail(value);
  };

  const onChangeIsNotARobot = (value: boolean) => {
    setIsSubscribing(undefined);
    setIsSubscribed(undefined);
    setIsNotARobot(value);
  };

  const onSubscribe = () => {
    const userLanguage =
      navigator.languages && navigator.languages.length
        ? navigator.languages[0]
        : navigator.language;
    setSubscribingRequestAnswer(undefined);
    setIsSubscribing(true);
    userMail &&
      madriwaService
        .subscribeToMailingList(userMail, userLanguage)
        .then((response) => {
          setSubscribingRequestAnswer('Usuario suscrito correctamente');
          setIsSubscribing(false);
          setIsSubscribed(true);
        })
        .catch((error: any) => {
          setSubscribingRequestAnswer(error.message ?? 'Error suscribiendo usuario');
          setIsSubscribing(false);
          setIsSubscribed(false);
        });
  };

  const temperaturesBlock = (): ReactNode => (
    <>
      {heatIslandInfo && (
        <div className="flex w-full flex-col items-center pb-3">
          <div
            className={`my-5 flex w-full flex-col items-center justify-center text-center font-normal leading-5 lg:w-1/4 ${
              isAHeatIsland ? 'text-isHeatIslandColor' : 'text-isNotHeatIslandColor'
            }`}
          >
            <HouseFill className="h-8 w-8" />
            <div className="mb-1 mt-2 text-[1.6rem] font-light">
              {heatIslandInfo.values.point.lts} ºC
            </div>
            <div>
              <b>Media</b>
              <br /> en tu casa
            </div>
          </div>
          <div className="mb-5 flex w-full justify-center lg:w-3/4">
            <ul className="flex w-full max-w-[28rem] py-3 text-madriwaGrey">
              <li className="mt-3 flex w-1/3 flex-col items-center justify-center text-center font-normal leading-5">
                <div className="mb-1 mt-2 text-[1.4rem] font-light">
                  {heatIslandInfo.values.mean} ºC
                </div>
                <div>
                  <b>Media</b>
                  <br /> en tu zona
                </div>
              </li>
              <li className="mt-3 flex w-1/3 flex-col items-center justify-center text-center font-normal leading-5">
                <div className="mb-1 mt-2 text-[1.4rem] font-light">
                  {heatIslandInfo.values.min} ºC
                </div>
                <div className="">
                  <b>Mínima</b>
                  <br /> en tu zona
                </div>
              </li>
              <li className="mt-3 flex w-1/3 flex-col items-center justify-center text-center font-normal leading-5">
                {' '}
                <div className="bold mb-1 mt-2 text-[1.4rem] font-light">
                  {heatIslandInfo.values.max} ºC
                </div>
                <div className="">
                  <b>Máxima</b>
                  <br /> en tu zona
                </div>
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );

  return (
    <Section
      className={`bg-resultBgColor leading-6 text-madriwaGrey transition-all duration-500 ease-out`}
    >
      {isRecoveringHeatIsland && (
        <div className="position-fixed flex h-full w-full items-center justify-center">
          <Preloader message="Recuperando datos ..." />
        </div>
      )}
      {!isRecoveringHeatIsland && heatIslandInfo && (
        <>
          <h1
            className={`flex flex-none items-center border-b border-dotted border-madriwaGreenLight pb-3 text-[0.9rem] leading-5 sm:text-[1.1rem]`}
          >
            <GeoAltFill className="mr-2 h-8 w-8 text-madriwaGreen sm:h-6 sm:w-6" />
            {locationName}
          </h1>
          <div className="flex-grow overflow-x-hidden overflow-y-scroll py-5 text-justify">
            <div className="flex w-full flex-col items-center text-[1rem] font-normal">
              <h1
                className={`mb-3 flex max-w-[100%] flex-col items-center justify-center p-3 pl-0 pt-6 text-center font-['Roboto_Condensed'] text-[1.6rem] font-bold leading-7 sm:max-w-[80%]  sm:flex-row ${
                  isAHeatIsland ? 'text-isHeatIslandColor' : 'text-isNotHeatIslandColor'
                }`}
              >
                {isAHeatIsland ? (
                  <ThermometerHigh className="mb-2 h-10 w-10" />
                ) : (
                  <ThermometerHalf className="mb-2 h-10 w-10" />
                )}
                <div className="mb-2 font-light">
                  {isAHeatIsland
                    ? 'Estás en una isla relativa de calor'
                    : 'No estás en una isla relativa de calor'}
                </div>
              </h1>
              <div className="max-w-[100%] sm:max-w-[80%]">
                <p className="px-5 pb-3">
                  Esto quiere decir que{' '}
                  <span
                    className={`text-[1rem] ${
                      isAHeatIsland ? 'text-isHeatIslandColor' : 'text-isNotHeatIslandColor'
                    }`}
                  >
                    {isAHeatIsland
                      ? 'en una noche calurosa estarás a más temperatura que la media de tus alrededores.'
                      : 'en una noche calurosa hay gente pasando más calor que tú en tus alrededores.'}
                  </span>
                </p>
                <p className="px-5 pb-3">
                  Esto puede ser por la orientación de tu casa, por falta de vegetación en tu zona,
                  por edificios oscuros o reflectantes en la zona. Es algo que cambia.
                </p>
                <p className="px-5 pb-3">
                  <span
                    className={`text-[1rem] ${
                      isAHeatIsland ? 'text-isHeatIslandColor' : 'text-isNotHeatIslandColor'
                    }`}
                  >
                    En concreto tienes{' '}
                    {formatToTwoDecimals(
                      isAHeatIsland
                        ? heatIslandInfo.values.point.lts - heatIslandInfo.values.mean
                        : heatIslandInfo.values.mean - heatIslandInfo.values.point.lts
                    )}{' '}
                    ºC {isAHeatIsland ? 'más' : 'menos'} que la media de tu zona
                  </span>
                </p>
              </div>
              {temperaturesBlock()}
            </div>
            <div className={`flex flex-col items-center justify-start pb-5`}>
              <div className="mb-10 w-full sm:w-2/4 sm:pr-3">
                <HeatIslandImage imageDataURI={croppedImage ?? undefined} />
                {/* Debugging purposes: check with non cropped raster image received from API */}
                {/* {heatIslandInfo.image && (
                  <img src={`data:image/jpeg;base64,${heatIslandInfo.image}`} alt="" />
                )} */}
              </div>
              <div className="mb-5 w-full text-center text-[1.2rem] font-normal sm:pl-3">
                <ul className="pb-5">
                  <li className="pb-3">
                    <a
                      href="https://madriwa.taniwa.es/blog/mejorar_isla_de_calor/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      ¿Qué puedes hacer para mejorar?
                      <BoxArrowUpRight className="relative bottom-[2px] ml-2 inline-block h-4 w-4 font-bold" />
                    </a>
                  </li>
                  <li className="pb-3">
                    <a
                      href="https://madriwa.taniwa.es/blog/como_calculamos_las_islas_de_calor/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      ¿Cómo se calcula todo esto?
                      <BoxArrowUpRight className="relative bottom-[2px] ml-2 inline-block h-4 w-4 font-bold" />
                    </a>
                  </li>
                  <li className="pb-3">
                    <a href="https://madriwa.taniwa.es/" target="_blank" rel="noreferrer">
                      ¿Quiénes somos?
                      <BoxArrowUpRight className="relative bottom-[2px] ml-2 inline-block h-4 w-4 font-bold" />
                    </a>
                  </li>
                </ul>
                <div className="fleex w-full items-center pr-5">
                  <div
                    className="flex w-full cursor-pointer items-center justify-center"
                    onClick={onTweetMessage}
                  >
                    <div className="flex items-center rounded-full bg-twitterColor px-5 py-2 text-white">
                      Twitéalo
                      <div className="ml-2 flex h-8 w-8 items-center justify-center">
                        <Twitter className="h-4 w-4 font-bold " />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Send by mail */}
            <div className={`mt-8 flex w-full flex-col items-center justify-center pb-10`}>
              <div className="flex w-full flex-col items-center p-5 text-madriwaGrey">
                <h1 className="mb-3 mb-3 text-center text-[1.3rem] font-light text-madriwaGreen sm:text-[1.5rem]">
                  ¿Te interesa conocer informaciones similares sobre Madrid?
                </h1>
                <div className="max-w-[28rem]">
                  <h2 className="mb-3 flex flex-col items-center font-bold leading-5 ">
                    <div className="relative mb-2 flex h-20 w-full items-center justify-center">
                      {isSubscribing ? (
                        <div className="flex items-center justify-center text-madriwaGreen">
                          <ArrowRepeat className="animate-spin text-xl" />
                        </div>
                      ) : isSubscribed === true ? (
                        <div
                          className="flex flex-col items-center justify-center text-madriwaGreen"
                          title={subscribingRequestAnswer}
                        >
                          <CheckCircleFill className="h-6 w-6" />
                          <div className="text-[0.8rem]">{subscribingRequestAnswer}</div>
                        </div>
                      ) : isSubscribed === false ? (
                        <div
                          className="flex flex-col items-center justify-center text-dangerColor"
                          title={subscribingRequestAnswer}
                        >
                          <XCircleFill className="h-6 w-6" />
                          <div className="text-[0.8rem]">{subscribingRequestAnswer}</div>
                        </div>
                      ) : (
                        <div className="h-10 w-10">
                          <Envelope className="relative h-full w-full -rotate-12" />
                        </div>
                      )}
                    </div>
                    Introduce tu email para suscribirte a nuestra mailing list:
                  </h2>
                  <TextField className="mb-3" labelPosition="top" onChange={onChangeEmail} />
                  <Checkbox
                    className="mb-3"
                    label="No soy un robot"
                    onChange={onChangeIsNotARobot}
                  />
                  <Button
                    className="w-full flex-none"
                    isDisabled={!isValidForm()}
                    size="small"
                    label="Suscribirse"
                    type="primary"
                    onClick={() => isValidForm() && onSubscribe()}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {!isRecoveringHeatIsland && !heatIslandInfo && (
        <div className="flex h-full w-full flex-col items-center justify-center text-center">
          <ExclamationTriangleFill className="h-10 w-10 pb-3 text-madriwaGreen" />
          <h1 className="mb-2 text-[1.2rem] font-light text-madriwaGreen sm:text-[1.5rem]">
            No se ha podido realizar la búsqueda
          </h1>
          <p className="text-[1.1rem] font-normal text-searchP">
            Recuerda que este servicio sólo funciona con la ciudad de Madrid.
            <br />
            Si te interesa para tu ciudad, escribe a{' '}
            <a className="underline" href="mailto:hola@taniwa.es">
              hola@taniwa.es
            </a>
          </p>
        </div>
      )}
      {!isRecoveringHeatIsland && (
        <Button
          type="secondary"
          className="flex-none"
          label="probar con otra dirección"
          onClick={onNewSearchHandler}
        />
      )}
    </Section>
  );
};

export default ResultSection;
